import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ytResolve, ytBulkImport } from '../lib/api';
import { Youtube, ArrowRight, Loader2, CheckSquare, Square, Flame, ExternalLink, Check } from 'lucide-react';
import { toast } from 'sonner';

const MAX_IMPORT = 25;

export default function MigrateYouTube() {
  const nav = useNavigate();
  const [url, setUrl] = useState('');
  const [channel, setChannel] = useState(null);
  const [videos, setVideos] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [done, setDone] = useState(null);

  const fetchChannel = async (e) => {
    e?.preventDefault();
    if (!url.startsWith('http')) return toast.error('Paste the full YouTube channel URL');
    setLoading(true); setChannel(null); setVideos([]); setSelected(new Set()); setDone(null);
    try {
      const d = await ytResolve(url);
      setChannel({ id: d.channel_id, title: d.channel_title, url: d.channel_url, note: d.note });
      setVideos(d.videos || []);
      // Pre-select the first 25
      const firstIds = new Set((d.videos || []).slice(0, MAX_IMPORT).map(v => v.video_id));
      setSelected(firstIds);
      toast.success(`Found ${d.videos.length} videos from ${d.channel_title}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not resolve channel');
    } finally { setLoading(false); }
  };

  const toggle = (vid) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(vid)) next.delete(vid);
      else if (next.size < MAX_IMPORT) next.add(vid);
      else toast.info(`You can select at most ${MAX_IMPORT} videos`);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size >= Math.min(videos.length, MAX_IMPORT)) setSelected(new Set());
    else setSelected(new Set(videos.slice(0, MAX_IMPORT).map(v => v.video_id)));
  };

  const runImport = async () => {
    if (selected.size === 0) return toast.error('Pick at least one video');
    setImporting(true);
    try {
      const chosen = videos.filter(v => selected.has(v.video_id));
      const res = await ytBulkImport(chosen);
      setDone(res);
      toast.success(`Imported ${res.count} videos. ${res.skipped?.length || 0} skipped as duplicates.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Import failed');
    } finally { setImporting(false); }
  };

  return (
    <div className="max-w-[1080px] mx-auto" data-testid="migrate-youtube-page">
      <div className="flex items-center gap-2 text-[#ff6b8a]"><Flame className="w-5 h-5" /><span className="uppercase text-[11px] tracking-widest font-bold">Migrate from YouTube</span></div>
      <h1 className="font-display text-[32px] md:text-[44px] mt-2 leading-[1.05]">Bring your channel to <span className="brand-gradient">VidCreForge</span>.</h1>
      <p className="text-white/65 text-[14.5px] mt-2 max-w-[720px]">Paste your YouTube channel URL. We&apos;ll list your public videos — pick up to {MAX_IMPORT} and we&apos;ll create them here instantly. No files are copied; each video plays via the official YouTube embed and keeps your view count intact.</p>

      <form onSubmit={fetchChannel} className="card-shell p-5 mt-8 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Youtube className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#ff0000]" />
          <input value={url} onChange={e => setUrl(e.target.value)} placeholder="https://youtube.com/@yourhandle" className="input-field w-full pl-9" data-testid="yt-channel-url-input" />
        </div>
        <button disabled={loading} className="btn-primary disabled:opacity-50" data-testid="yt-resolve-btn">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />} {loading ? 'Fetching…' : 'Find videos'}
        </button>
      </form>

      {channel && (
        <section className="mt-8" data-testid="yt-channel-result">
          <div className="card-shell p-5 flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-[12px] uppercase tracking-wider text-white/45">Channel</div>
              <div className="font-display text-[22px] mt-0.5">{channel.title}</div>
              <a href={channel.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[12.5px] text-[#22d3ee] mt-1 hover:underline">
                {channel.url} <ExternalLink className="w-3 h-3" />
              </a>
              {channel.note && <div className="text-[12px] text-[#ffcf52] mt-2">{channel.note}</div>}
            </div>
            <div className="text-right">
              <div className="text-[12px] uppercase tracking-wider text-white/45">Selected</div>
              <div className="font-display text-[26px]">{selected.size} / {MAX_IMPORT}</div>
              <button onClick={toggleAll} className="mt-1 text-[12px] text-[#ff6b8a] hover:underline" data-testid="yt-select-all-btn">
                {selected.size >= Math.min(videos.length, MAX_IMPORT) ? 'Clear selection' : `Select first ${Math.min(videos.length, MAX_IMPORT)}`}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mt-5">
            {videos.map((v, i) => {
              const on = selected.has(v.video_id);
              const disabled = !on && selected.size >= MAX_IMPORT;
              return (
                <button key={v.video_id} onClick={() => toggle(v.video_id)} disabled={disabled}
                  className={`card-shell overflow-hidden text-left transition group ${on ? 'ring-2 ring-[#22d3ee]' : 'hover:ring-1 hover:ring-white/30'} ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
                  data-testid={`yt-video-${i}`}>
                  <div className="relative aspect-video bg-black">
                    <img src={v.thumb} alt={v.title} className="w-full h-full object-cover" loading="lazy" />
                    <div className="absolute top-2 right-2">
                      {on ? <div className="w-6 h-6 rounded-md grid place-items-center" style={{ background: '#22d3ee', color: '#0a0a0c' }}><Check className="w-4 h-4" strokeWidth={3} /></div>
                          : <div className="w-6 h-6 rounded-md border-2 border-white/60 bg-black/40" />}
                    </div>
                  </div>
                  <div className="p-2.5">
                    <div className="text-[12.5px] font-semibold line-clamp-2">{v.title}</div>
                    <div className="text-[11px] text-white/45 mt-0.5 font-mono">{v.video_id}</div>
                  </div>
                </button>
              );
            })}
          </div>

          <div className="card-shell p-5 mt-6 flex flex-col sm:flex-row items-center justify-between gap-3 sticky bottom-3" style={{ background: 'rgba(10,10,12,0.92)', backdropFilter: 'blur(12px)' }}>
            <div className="text-[13px] text-white/70 flex items-center gap-2">
              <CheckSquare className="w-4 h-4 text-[#22d3ee]" />
              By importing you confirm you own or have rights to these videos. VidCreForge removes infringing content on DMCA notice.
            </div>
            <button disabled={importing || selected.size === 0} onClick={runImport} className="btn-primary disabled:opacity-50" data-testid="yt-import-btn">
              {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Flame className="w-4 h-4" />}
              {importing ? 'Importing…' : `Import ${selected.size} video${selected.size === 1 ? '' : 's'}`}
            </button>
          </div>
        </section>
      )}

      {done && (
        <section className="card-shell p-6 mt-8 text-center" data-testid="yt-import-done">
          <div className="w-14 h-14 mx-auto rounded-full grid place-items-center" style={{ background: 'rgba(34,211,238,0.15)', border: '1px solid rgba(34,211,238,0.4)' }}>
            <Check className="w-7 h-7 text-[#22d3ee]" />
          </div>
          <h2 className="font-display text-[26px] mt-3">Forge complete — {done.count} video{done.count === 1 ? '' : 's'} live</h2>
          {done.skipped?.length ? <p className="text-white/60 text-[13px] mt-1">{done.skipped.length} skipped as already imported.</p> : null}
          <div className="flex items-center justify-center gap-2 mt-5">
            <button onClick={() => nav('/studio')} className="btn-primary">Open Creator Studio</button>
            <button onClick={() => { setChannel(null); setVideos([]); setSelected(new Set()); setDone(null); setUrl(''); }} className="btn-ghost">Migrate another channel</button>
          </div>
        </section>
      )}
    </div>
  );
}
